import { PostgrestResponse } from '@supabase/supabase-js';
import { IUsuariosRfid } from '../../../types/tables/IUsuariosRfid';
import { BaseMultiTenantService } from '../base/BaseMultiTenantService';

/**
 * Serviço para gerenciamento de usuários RFID com isolamento multi-tenant
 * Gerencia cartões e pulseiras RFID para acesso rápido ao sistema
 */
export class UsuariosRfidService extends BaseMultiTenantService<IUsuariosRfid> {
     constructor() {
          super('usuarios_rfid', ['codigo_rfid'], 'fk_id_organizacao');
     }

     /**
      * Busca RFID por código
      */
     async getByCodigo(organizationId: number, codigo: string): Promise<PostgrestResponse<IUsuariosRfid & { pessoa?: any }>> {
          return await this.client
               .from(this.tableName)
               .select(
                    `
                    *,
                    pessoa:fk_id_pessoa(
                         id,
                         nome,
                         email,
                         fk_id_tipo_pessoa,
                         tipo_pessoa:fk_id_tipo_pessoa(nome)
                    )
               `,
               )
               .eq(this.organizationIdField, organizationId)
               .eq('codigo_rfid', codigo)
               .eq('ativo', true)
               .is('deleted_at', null)
               .single();
     }

     /**
      * Busca RFID por pessoa
      */
     async getByPessoa(organizationId: number, pessoaId: number): Promise<PostgrestResponse<IUsuariosRfid>> {
          return await this.client.from(this.tableName).select('*').eq(this.organizationIdField, organizationId).eq('fk_id_pessoa', pessoaId).is('deleted_at', null).single();
     }

     /**
      * Autentica usuário via RFID
      */
     async autenticarRfid(
          organizationId: number,
          codigo: string,
     ): Promise<{
          success: boolean;
          pessoa?: any;
          saldo?: number;
          message?: string;
     }> {
          try {
               const { data: rfidDataRaw } = await this.getByCodigo(organizationId, codigo);
               const rfidData = rfidDataRaw as any; // Conversão temporária para resolver problemas de tipagem

               if (!rfidData) {
                    return {
                         success: false,
                         message: 'Código RFID não encontrado',
                    };
               }

               if (!rfidData.ativo) {
                    return {
                         success: false,
                         message: 'RFID desativado',
                    };
               }

               if (!rfidData.pessoa?.ativo) {
                    return {
                         success: false,
                         message: 'Usuário desativado',
                    };
               }

               // Buscar saldo se for aluno
               let saldo = 0;
               if (rfidData.pessoa?.tipo_pessoa?.nome === 'Aluno') {
                    const { data: saldoData } = await this.client.from('saldos_alunos').select('saldo_disponivel').eq('fk_id_aluno', rfidData.pessoa.id).eq('fk_id_organizacao', organizationId).maybeSingle();

                    saldo = saldoData?.saldo_disponivel || 0;
               } else if (rfidData.pessoa?.tipo_pessoa?.nome === 'Responsável') {
                    const { data: saldoData } = await this.client.from('saldos_responsaveis').select('saldo_atual').eq('fk_id_responsavel', rfidData.pessoa.id).eq('fk_id_organizacao', organizationId).maybeSingle();

                    saldo = saldoData?.saldo_atual || 0;
               }

               // Atualizar última utilização (usando campo genérico updated_at)
               await this.update(rfidData.id, { updated_at: new Date().toISOString() }, organizationId);

               return {
                    success: true,
                    pessoa: rfidData.pessoa,
                    saldo,
               };
          } catch (error) {
               console.error('Erro na autenticação RFID:', error);
               return {
                    success: false,
                    message: 'Erro interno na autenticação',
               };
          }
     }

     /**
      * Vincula RFID a uma pessoa
      */
     async vincularRfid(organizationId: number, pessoaId: number, codigoRfid: string, userId?: number): Promise<PostgrestResponse<IUsuariosRfid[]>> {
          // Verificar se o código já está em uso
          const { data: existente } = await this.client.from(this.tableName).select('id').eq(this.organizationIdField, organizationId).eq('codigo_rfid', codigoRfid).is('deleted_at', null).maybeSingle();

          if (existente) {
               throw new Error('Código RFID já está em uso');
          }

          // Verificar se a pessoa já possui RFID
          const { data: rfidPessoaRaw } = await this.getByPessoa(organizationId, pessoaId);
          const rfidPessoa = rfidPessoaRaw as any; // Conversão temporária

          if (rfidPessoa) {
               // Atualizar RFID existente
               return await this.update(
                    rfidPessoa.id,
                    {
                         codigo_rfid: codigoRfid,
                         ativo: true,
                    },
                    organizationId,
                    userId,
               );
          } else {
               // Criar novo RFID
               return await this.create(
                    {
                         fk_id_pessoa: pessoaId,
                         codigo_rfid: codigoRfid,
                         ativo: true,
                    },
                    organizationId,
                    userId,
               );
          }
     }

     /**
      * Desvincula RFID de uma pessoa
      */
     async desvincularRfid(organizationId: number, pessoaId: number, userId?: number): Promise<PostgrestResponse<IUsuariosRfid[]>> {
          const { data: rfidDataRaw } = await this.getByPessoa(organizationId, pessoaId);
          const rfidData = rfidDataRaw as any; // Conversão temporária

          if (!rfidData) {
               throw new Error('Pessoa não possui RFID vinculado');
          }

          // softDelete retorna um formato diferente, vou usar update para desativar
          return await this.update(rfidData.id, { ativo: false, deleted_at: new Date().toISOString() }, organizationId, userId);
     }

     /**
      * Ativa/desativa RFID
      */
     async toggleAtivo(organizationId: number, id: number, userId?: number): Promise<PostgrestResponse<IUsuariosRfid[]>> {
          const { data: rfidData } = await this.getById(id, organizationId);

          if (!rfidData || Array.isArray(rfidData)) {
               throw new Error('RFID não encontrado');
          }

          const rfid = rfidData as IUsuariosRfid;

          return await this.update(id, { ativo: !rfid.ativo }, organizationId, userId);
     }

     /**
      * Método removido - use ViewAdminRfid para consultas complexas com informações das pessoas
      * Para consultas simples na tabela usuarios_rfid, use os métodos herdados
      */

     /**
      * Relatório de uso de RFIDs
      */
     async getRelatorioUso(organizationId: number, dataInicio?: string, dataFim?: string): Promise<PostgrestResponse<IUsuariosRfid[]>> {
          let query = this.client
               .from(this.tableName)
               .select(
                    `
                    *,
                    pessoa:fk_id_pessoa(nome)
               `,
               )
               .eq(this.organizationIdField, organizationId)
               .not('ultima_utilizacao', 'is', null)
               .order('ultima_utilizacao', { ascending: false });

          if (dataInicio) {
               query = query.gte('ultima_utilizacao', dataInicio);
          }

          if (dataFim) {
               query = query.lte('ultima_utilizacao', dataFim);
          }

          return await query;
     }

     /**
      * Gera código RFID único para a organização
      */
     async gerarCodigoUnico(organizationId: number): Promise<string> {
          let tentativas = 0;
          const maxTentativas = 10;

          while (tentativas < maxTentativas) {
               // Gerar código aleatório de 8 dígitos
               const codigo = Math.random().toString().slice(2, 10);

               // Verificar se já existe
               const { data: existente } = await this.client.from(this.tableName).select('id').eq(this.organizationIdField, organizationId).eq('codigo_rfid', codigo).maybeSingle();

               if (!existente) {
                    return codigo;
               }

               tentativas++;
          }

          throw new Error('Não foi possível gerar código único após várias tentativas');
     }
}

export const usuariosRfidService = new UsuariosRfidService();
